import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ShoppingCart,
  Payment as PaymentIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { getApiUrl } from "../config/api";
import { validarCarrinho } from "../utils/produtoRegras";

/**
 * Implementação compartilhada da página de inscrições e contribuições.
 *
 * É usada tanto pela rota pública (`/inscricoes`) quanto pela rota exclusiva,
 * não listada no menu (`/inscricoes-exclusivas`).
 *
 * @param {Object}   props
 * @param {Function} [props.filtrarProdutos] Predicado opcional aplicado à lista
 *   de produtos vinda da API (`Array.prototype.filter`). Quando informado, só os
 *   produtos para os quais retornar `true` ficam disponíveis. Quando omitido,
 *   todos os produtos ativos são exibidos.
 * @param {string}   [props.titulo] Título exibido no topo da página.
 * @param {React.ReactNode} [props.aviso] Conteúdo opcional exibido como um
 *   alerta de atenção (warning) logo abaixo do título. Quando omitido, nenhum
 *   aviso é renderizado.
 */
export const InscricoesBase = ({
  filtrarProdutos,
  titulo = "Inscrições e Contribuições",
  aviso,
}) => {
  const [produtos, setProdutos] = useState([]);
  const [carrinho, setCarrinho] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    nomeInscrito: "",
    produtoId: "",
  });
  const [resumoCompra, setResumoCompra] = useState({
    nomePagador: "",
    emailInscrito: "",
    telefoneInscrito: "",
    observacoes: "",
    formaPagamento: "pix", // Valor padrão
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPagamento, setShowPagamento] = useState(false);
  const [dadosPagamento, setDadosPagamento] = useState(null);
  const [showProdutosInfo, setShowProdutosInfo] = useState(false);
  const [emailValido, setEmailValido] = useState(false);
  const [pollingAtivo, setPollingAtivo] = useState(false);
  const [statusPagamento, setStatusPagamento] = useState(null);
  const [inscricaoAprovada, setInscricaoAprovada] = useState(null);
  const [tentativasPolling, setTentativasPolling] = useState(0);
  const [pixCopiado, setPixCopiado] = useState(false);
  const [levaCrianca, setLevaCrianca] = useState(false);
  const [criancas, setCriancas] = useState([{ nome: "", idade: "" }]);

  // Erros de quantidade mínima/múltiplos por produto (regras em produtoRegras).
  // Carrinho válido = lista vazia.
  const errosCarrinho = useMemo(() => validarCarrinho(carrinho), [carrinho]);

  // Regra: criança acima de 12 anos paga inscrição (deve entrar como inscrito).
  const IDADE_LIMITE_CRIANCA = 12;
  const idadeAcimaDoLimite = (crianca) => {
    const idadeNum = parseInt(crianca?.idade, 10);
    return Number.isFinite(idadeNum) && idadeNum > IDADE_LIMITE_CRIANCA;
  };
  const temCriancaAcimaDoLimite =
    levaCrianca && criancas.some(idadeAcimaDoLimite);

  const TOGLE_SHOW_PRODUCTS_INFO = false; // Variável para controlar exibição do botão de informações dos produtos
  // Buscar produtos disponíveis
  useEffect(() => {
    buscarProdutos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrarProdutos]);

  // Tratar o retorno do Checkout Pro (Mercado Pago) via ?status= na URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (!status) return;

    if (status === "sucesso") {
      setSuccess("Pagamento aprovado! Sua inscrição foi confirmada.");
    } else if (status === "pendente") {
      setSuccess(
        "Pagamento pendente. Assim que for confirmado, sua inscrição será validada.",
      );
    } else if (status === "falha") {
      setError(
        "Não foi possível concluir o pagamento. Você pode tentar novamente.",
      );
    }

    // Remove o parâmetro da URL para não repetir a mensagem ao recarregar.
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const buscarProdutos = async () => {
    try {
      const response = await fetch(getApiUrl("/api/checkout/produtos"));
      const result = await response.json();

      if (result.success) {
        // Aplica o filtro específico da página (ex.: catálogo de valores da
        // rota exclusiva). Sem filtro, exibe todos os produtos retornados.
        const lista =
          typeof filtrarProdutos === "function"
            ? result.data.filter(filtrarProdutos)
            : result.data;
        setProdutos(lista);
      } else {
        setError("Erro ao carregar produtos");
      }
    } catch (error) {
      setError("Erro ao carregar produtos");
      console.error("Erro:", error);
    }
  };

  // Adicionar item ao carrinho
  const adicionarAoCarrinho = () => {
    if (!currentItem.nomeInscrito || !currentItem.produtoId) {
      setError("Nome do inscrito e produto são obrigatórios");
      return;
    }

    const produto = produtos.find((p) => p._id === currentItem.produtoId);

    const novoItem = {
      id: Date.now(),
      nomeInscrito: currentItem.nomeInscrito,
      produto,
      quantidade: 1,
      subtotal: produto.valor,
    };

    setCarrinho([...carrinho, novoItem]);
    setCurrentItem({
      nomeInscrito: "",
      produtoId: "",
    });
    setError("");
    setSuccess("Item adicionado ao carrinho!");
    setTimeout(() => setSuccess(""), 3000);
  };

  // Remover item do carrinho
  const removerDoCarrinho = (itemId) => {
    setCarrinho(carrinho.filter((item) => item.id !== itemId));
  };

  // Calcular total
  const calcularTotal = () => {
    return carrinho.reduce((total, item) => total + item.subtotal, 0);
  };

  // Validar email
  const validarEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Handler para mudança no email
  const handleEmailChange = (e) => {
    const email = e.target.value;
    const isValid = email ? validarEmail(email) : false;
    setEmailValido(isValid);
    setResumoCompra({
      ...resumoCompra,
      emailInscrito: email,
    });
  };

  // Função para copiar o código PIX
  const copiarCodigoPix = async () => {
    try {
      await navigator.clipboard.writeText(dadosPagamento?.qrCode || "");
      setPixCopiado(true);
      // Remove o feedback após 2 segundos
      setTimeout(() => setPixCopiado(false), 2000);
    } catch (err) {
      console.error("Erro ao copiar PIX:", err);
      // Fallback para navegadores que não suportam clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = dadosPagamento?.qrCode || "";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setPixCopiado(true);
      setTimeout(() => setPixCopiado(false), 2000);
    }
  };

  // Polling para verificar status do pagamento
  const verificarStatusPagamento = async (paymentId) => {
    try {
      const response = await fetch(getApiUrl("/api/pagamentos/verificar"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ paymentId }),
      });

      const result = await response.json();

      if (result.success) {
        setStatusPagamento(result.data.status);

        if (result.data.status === "approved") {
          setInscricaoAprovada(result.data.inscricao);
          setPollingAtivo(false);
          return true; // Pagamento aprovado
        }
      }

      return false; // Ainda não aprovado
    } catch (error) {
      console.error("Erro ao verificar pagamento:", error);
      return false;
    }
  };

  // Iniciar polling após usuário clicar em "Realizei o pagamento"
  const iniciarPolling = async () => {
    if (!dadosPagamento?.paymentId) return;

    setPollingAtivo(true);
    setTentativasPolling(0);

    // 5 tentativas durante 10 segundos (uma a cada 2 segundos)
    for (let i = 0; i < 5; i++) {
      setTentativasPolling(i + 1);

      const aprovado = await verificarStatusPagamento(dadosPagamento.paymentId);

      if (aprovado) {
        return; // Sair do loop se aprovado
      }

      // Aguardar 2 segundos antes da próxima tentativa
      if (i < 4) {
        // Não aguardar na última tentativa
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // Se chegou aqui, não foi aprovado após 5 tentativas
    setPollingAtivo(false);
    setStatusPagamento("timeout");
  };

  // Fechar modal e resetar estados
  const fecharModal = () => {
    setShowPagamento(false);
    setDadosPagamento(null);
    setPollingAtivo(false);
    setStatusPagamento(null);
    setInscricaoAprovada(null);
    setTentativasPolling(0);
  };

  // Crianças acompanhantes
  const handleLevaCriancaChange = (e) => {
    const marcado = e.target.checked;
    setLevaCrianca(marcado);
    if (marcado && criancas.length === 0) {
      setCriancas([{ nome: "", idade: "" }]);
    }
  };

  const atualizarCrianca = (index, campo, valor) => {
    setCriancas((prev) =>
      prev.map((crianca, i) =>
        i === index ? { ...crianca, [campo]: valor } : crianca,
      ),
    );
  };

  const adicionarCrianca = () => {
    setCriancas((prev) => [...prev, { nome: "", idade: "" }]);
  };

  const removerCrianca = (index) => {
    setCriancas((prev) => prev.filter((_, i) => i !== index));
  };

  // Processar pagamento
  const processarPagamento = async () => {
    if (carrinho.length === 0) {
      setError("Adicione itens ao carrinho primeiro");
      return;
    }

    // Bloqueia a finalização se algum produto não atende à sua regra de quantidade.
    const erros = validarCarrinho(carrinho);
    if (erros.length > 0) {
      setError(erros[0]);
      return;
    }

    // Crianças acompanhantes (só quando a opção está marcada).
    const criancasValidas = levaCrianca
      ? criancas
          .map((c) => ({ nome: c.nome.trim(), idade: String(c.idade).trim() }))
          .filter((c) => c.nome)
      : [];

    if (levaCrianca && criancasValidas.length === 0) {
      setError(
        "Informe o nome de pelo menos uma criança ou desmarque a opção de levar criança.",
      );
      return;
    }

    if (temCriancaAcimaDoLimite) {
      setError(
        `Criança acima de ${IDADE_LIMITE_CRIANCA} anos paga inscrição e deve ser adicionada como inscrito (não como criança). Ajuste as idades para prosseguir.`,
      );
      return;
    }

    // Usar o email de contato especificado
    const emailPrincipal = resumoCompra.emailInscrito;

    setLoading(true);
    setError("");

    try {
      // 1. Criar inscrição
      const inscricaoData = {
        nomePagante: resumoCompra.nomePagador, // Usar nome do pagador
        emailPagante: emailPrincipal,
        telefonePagante: resumoCompra.telefoneInscrito,
        observacoes: resumoCompra.observacoes,
        criancas: criancasValidas,
        formaPagamento: resumoCompra.formaPagamento,
        inscricoes: carrinho.map((item) => ({
          produtoId: item.produto._id,
          nomeInscrito: item.nomeInscrito,
        })),
      };

      const inscricaoResponse = await fetch(getApiUrl("/api/inscricoes"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(inscricaoData),
      });

      const inscricaoResult = await inscricaoResponse.json();

      if (!inscricaoResult.success) {
        throw new Error(inscricaoResult.error);
      }

      // 2. Se for PIX, criar pagamento online. Caso contrário, apenas confirmar inscrição pendente
      if (resumoCompra.formaPagamento === "pix") {
        const pagamentoResponse = await fetch(
          getApiUrl("/api/pagamentos/criar"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              inscricaoId: inscricaoResult.data._id,
            }),
          },
        );

        const pagamentoResult = await pagamentoResponse.json();

        if (!pagamentoResult.success) {
          throw new Error(pagamentoResult.error);
        }

        setDadosPagamento(pagamentoResult.data);
        setShowPagamento(true);
      } else if (resumoCompra.formaPagamento === "cartao_credito") {
        // Cartão: cria uma preferência (Checkout Pro) e redireciona o usuário
        // para a tela de pagamento hospedada pelo Mercado Pago.
        const preferenciaResponse = await fetch(
          getApiUrl("/api/pagamentos?action=criar-preferencia"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              inscricaoId: inscricaoResult.data._id,
              returnUrl: `${window.location.origin}${window.location.pathname}`,
            }),
          },
        );

        const preferenciaResult = await preferenciaResponse.json();

        if (!preferenciaResult.success) {
          throw new Error(preferenciaResult.error);
        }

        // Redireciona para o checkout do Mercado Pago (a página sai do ar aqui).
        window.location.href = preferenciaResult.data.initPoint;
        return;
      } else {
        // Dinheiro: confirmação de inscrição pendente (pagamento manual).
        setInscricaoAprovada({
          ...inscricaoResult.data,
          isPendente: true,
          formaPagamento: resumoCompra.formaPagamento,
        });
        setShowPagamento(true);
      }

      // Limpar carrinho após sucesso
      setCarrinho([]);
      setResumoCompra({
        emailInscrito: "",
        telefoneInscrito: "",
        observacoes: "",
        formaPagamento: "pix",
      });
      setLevaCrianca(false);
      setCriancas([{ nome: "", idade: "" }]);
    } catch (error) {
      setError(error.message || "Erro ao processar pagamento");
      console.error("Erro:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        flexGrow: 1,
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 3 },
      }}
    >
      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        sx={{
          textAlign: "center",
          color: "primary.main",
          fontWeight: "bold",
          mb: 4,
          fontSize: { xs: "1.8rem", md: "2.5rem" },
        }}
      >
        {titulo}
      </Typography>

      {aviso && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {aviso}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={{ xs: 3, md: 4 }}>
        {/* Formulário Principal */}
        <Grid item sx={{ width: { xs: "100%", md: "62%" } }}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: "primary.main", mb: 3 }}
              >
                Adicionar Inscrição
              </Typography>

              <Grid container spacing={3}>
                {/* Campos básicos */}
                <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                  <TextField
                    fullWidth
                    label="Nome do Inscrito"
                    value={currentItem.nomeInscrito}
                    onChange={(e) =>
                      setCurrentItem({
                        ...currentItem,
                        nomeInscrito: e.target.value,
                      })
                    }
                    required
                  />
                </Grid>

                <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                  <FormControl fullWidth>
                    <InputLabel>Selecione o Produto/Evento *</InputLabel>
                    <Select
                      value={currentItem.produtoId}
                      onChange={(e) =>
                        setCurrentItem({
                          ...currentItem,
                          produtoId: e.target.value,
                        })
                      }
                      required
                    >
                      {produtos.map((produto) => (
                        <MenuItem key={produto._id} value={produto._id}>
                          {produto.nome} - {produto.subtipo} - R${" "}
                          {produto.valor.toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Botão de Informações dos Produtos */}
                  {TOGLE_SHOW_PRODUCTS_INFO && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<InfoIcon />}
                      onClick={() => setShowProdutosInfo(true)}
                      sx={{
                        mt: 1,
                        borderColor: "primary.main",
                        color: "primary.main",
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        textTransform: "none",
                        borderRadius: 2,
                        "&:hover": {
                          backgroundColor: "rgba(139, 38, 53, 0.08)",
                          borderColor: "primary.dark",
                        },
                      }}
                    >
                      Ver Preços e Detalhes dos Produtos
                    </Button>
                  )}
                </Grid>

                <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={adicionarAoCarrinho}
                    size="large"
                    sx={{
                      bgcolor: "primary.main",
                      "&:hover": { bgcolor: "primary.dark" },
                      py: { xs: 2, sm: 1.5 },
                    }}
                    disabled={
                      !currentItem.nomeInscrito || !currentItem.produtoId
                    }
                  >
                    Adicionar ao Carrinho
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Dados para Finalização */}
          {carrinho.length > 0 && (
            <Card>
              <CardContent>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ color: "primary.main" }}
                >
                  Dados para Finalização
                </Typography>

                <Grid container spacing={3}>
                  <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                    <TextField
                      fullWidth
                      label="Email para Contato"
                      type="email"
                      value={resumoCompra.emailInscrito}
                      onChange={handleEmailChange}
                      required
                      error={resumoCompra.emailInscrito && !emailValido}
                      helperText={
                        resumoCompra.emailInscrito && !emailValido
                          ? "Por favor, insira um email válido"
                          : "Este email será usado para confirmações de pagamento"
                      }
                    />
                  </Grid>

                  <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                    <TextField
                      fullWidth
                      label="Nome do Pagador"
                      type="text"
                      value={resumoCompra.nomePagador}
                      onChange={(e) =>
                        setResumoCompra({
                          ...resumoCompra,
                          nomePagador: e.target.value,
                        })
                      }
                      helperText="Nome do pagador para referência"
                    />
                  </Grid>

                  <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                    <TextField
                      fullWidth
                      label="Telefone para Contato (Opcional)"
                      type="tel"
                      value={resumoCompra.telefoneInscrito}
                      onChange={(e) =>
                        setResumoCompra({
                          ...resumoCompra,
                          telefoneInscrito: e.target.value,
                        })
                      }
                      helperText="Telefone para contato em caso de necessidade"
                    />
                  </Grid>

                  <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                    <FormControl fullWidth>
                      <InputLabel>Forma de Pagamento</InputLabel>
                      <Select
                        value={resumoCompra.formaPagamento}
                        onChange={(e) =>
                          setResumoCompra({
                            ...resumoCompra,
                            formaPagamento: e.target.value,
                          })
                        }
                        required
                      >
                        <MenuItem value="pix">PIX</MenuItem>
                        <MenuItem value="cartao_credito">
                          Cartão de Crédito
                        </MenuItem>
                        <MenuItem value="dinheiro">Dinheiro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Crianças acompanhantes */}
                  <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={levaCrianca}
                            onChange={handleLevaCriancaChange}
                            sx={{
                              color: "primary.main",
                              "&.Mui-checked": { color: "primary.main" },
                            }}
                          />
                        }
                        label="Vou levar criança(s)"
                        sx={{ mr: 0.5 }}
                      />
                      <Tooltip
                        title={`Crianças acima de ${IDADE_LIMITE_CRIANCA} anos pagam inscrição e devem ser adicionadas como inscrito (na lista de produtos acima), não aqui.`}
                        arrow
                      >
                        <InfoIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", cursor: "help" }}
                        />
                      </Tooltip>
                    </Box>

                    {levaCrianca && (
                      <Box sx={{ mt: 1 }}>
                        {criancas.map((crianca, index) => {
                          const acimaLimite = idadeAcimaDoLimite(crianca);
                          return (
                            <Box
                              key={index}
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "flex-start",
                                mb: 2,
                              }}
                            >
                              <TextField
                                fullWidth
                                label={`Nome da criança ${index + 1}`}
                                value={crianca.nome}
                                onChange={(e) =>
                                  atualizarCrianca(index, "nome", e.target.value)
                                }
                              />
                              <Tooltip
                                title={
                                  acimaLimite
                                    ? `Acima de ${IDADE_LIMITE_CRIANCA} anos paga inscrição. Adicione como inscrito na lista de produtos, não como criança.`
                                    : ""
                                }
                                arrow
                                open={acimaLimite || undefined}
                              >
                                <TextField
                                  label="Idade"
                                  type="number"
                                  value={crianca.idade}
                                  onChange={(e) =>
                                    atualizarCrianca(
                                      index,
                                      "idade",
                                      e.target.value,
                                    )
                                  }
                                  error={acimaLimite}
                                  helperText={
                                    acimaLimite
                                      ? `Acima de ${IDADE_LIMITE_CRIANCA} anos paga inscrição`
                                      : ""
                                  }
                                  sx={{ width: 140, flexShrink: 0 }}
                                  inputProps={{ min: 0 }}
                                />
                              </Tooltip>
                              {criancas.length > 1 && (
                                <IconButton
                                  onClick={() => removerCrianca(index)}
                                  sx={{ color: "#d32f2f", mt: 1 }}
                                  aria-label="Remover criança"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}

                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={adicionarCrianca}
                          sx={{
                            borderColor: "primary.main",
                            color: "primary.main",
                            textTransform: "none",
                            "&:hover": { borderColor: "primary.dark" },
                          }}
                        >
                          Adicionar outra criança
                        </Button>
                      </Box>
                    )}
                  </Grid>

                  <Grid item sx={{ width: { xs: "100%", md: "100%" } }}>
                    <TextField
                      fullWidth
                      label="Observações (Opcional)"
                      multiline
                      rows={2}
                      value={resumoCompra.observacoes}
                      onChange={(e) =>
                        setResumoCompra({
                          ...resumoCompra,
                          observacoes: e.target.value,
                        })
                      }
                      helperText="Informações adicionais ou observações sobre a inscrição"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Carrinho Simplificado */}
        <Grid item sx={{ width: { xs: "100%", md: "35%" } }}>
          <Card sx={{ position: { md: "sticky" }, top: 20 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h5"
                sx={{
                  color: "primary.main",
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                <ShoppingCart sx={{ mr: 1, verticalAlign: "middle" }} />
                Carrinho ({carrinho.length})
              </Typography>

              {carrinho.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: { xs: 3, md: 4 },
                    color: "#999",
                  }}
                >
                  <Typography variant="body1">Carrinho vazio</Typography>
                  <Typography variant="body2">
                    Adicione produtos acima
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 3 }}>
                    {carrinho.map((item, index) => (
                      <Box
                        key={item.id}
                        sx={{
                          p: { xs: 1.5, md: 2 },
                          mb: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 1, sm: 0 },
                          }}
                        >
                          <Box sx={{ flex: 1, mr: { xs: 0, sm: 2 } }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: "bold",
                                mb: 1,
                                fontSize: { xs: "0.875rem", md: "1rem" },
                              }}
                            >
                              {item.produto.nome}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 0.5,
                                fontSize: { xs: "0.75rem", md: "0.875rem" },
                              }}
                            >
                              Tipo Inscrição: {item.produto.subtipo}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 0.5,
                                fontSize: { xs: "0.75rem", md: "0.875rem" },
                              }}
                            >
                              Inscrito: {item.nomeInscrito}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              textAlign: { xs: "left", sm: "right" },
                              display: "flex",
                              alignItems: "center",
                              justifyContent: {
                                xs: "space-between",
                                sm: "flex-end",
                              },
                              gap: { xs: 2, sm: 1 },
                              width: { xs: "100%", sm: "auto" },
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: "primary.main",
                                fontWeight: "bold",
                                fontSize: { xs: "1rem", md: "1.25rem" },
                              }}
                            >
                              R$ {item.subtotal.toFixed(2)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removerDoCarrinho(item.id)}
                              sx={{
                                color: "#d32f2f",
                                minWidth: { xs: "36px", md: "auto" },
                                minHeight: { xs: "36px", md: "auto" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        fontSize: { xs: "1.75rem", md: "2.125rem" },
                      }}
                    >
                      R$ {calcularTotal().toFixed(2)}
                    </Typography>
                  </Box>

                  {errosCarrinho.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
                      {errosCarrinho.map((mensagem, index) => (
                        <Typography key={index} variant="body2">
                          {mensagem}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {emailValido && (
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<PaymentIcon />}
                      onClick={processarPagamento}
                      disabled={
                        loading ||
                        errosCarrinho.length > 0 ||
                        temCriancaAcimaDoLimite
                      }
                      sx={{
                        bgcolor: "#28a745",
                        "&:hover": { bgcolor: "#218838" },
                        py: { xs: 2, md: 1.5 },
                        fontSize: { xs: "1rem", md: "1rem" },
                      }}
                    >
                      {loading ? "Processando..." : "Finalizar Pagamento"}
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modal de Pagamento */}
      <Dialog
        open={showPagamento}
        onClose={() => setShowPagamento(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", color: "primary.main" }}>
          {inscricaoAprovada?.isPendente
            ? `Inscrição Confirmada - ${inscricaoAprovada.formaPagamento === "cartao_credito" ? "Cartão de Crédito" : "Dinheiro"}`
            : "Pagamento PIX"}
        </DialogTitle>
        <DialogContent>
          {dadosPagamento && !inscricaoAprovada && (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" gutterBottom>
                Valor: R$ {dadosPagamento.valorTotal.toFixed(2)}
              </Typography>

              {dadosPagamento.qrCodeImage && (
                <Box sx={{ my: 3 }}>
                  <img
                    src={`data:image/png;base64,${dadosPagamento.qrCodeImage}`}
                    alt="QR Code PIX"
                    style={{ maxWidth: "300px", width: "100%" }}
                  />
                </Box>
              )}

              {dadosPagamento.qrCode && (
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Código PIX (Copia e Cola):
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={dadosPagamento.qrCode}
                      InputProps={{ readOnly: true }}
                      sx={{
                        "& .MuiInputBase-input": { fontSize: "0.8rem" },
                        width: "100%",
                      }}
                    />
                    <Button
                      variant="contained"
                      size="medium"
                      startIcon={<CopyIcon />}
                      onClick={copiarCodigoPix}
                      sx={{
                        minWidth: "50%",
                        height: "30px", // Mesmo altura do TextField
                        backgroundColor: pixCopiado ? "#4caf50" : "#1976d2",
                        color: "white",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: pixCopiado ? "#45a049" : "#1565c0",
                        },
                      }}
                    >
                      {pixCopiado ? "Copiado!" : "Copiar PIX"}
                    </Button>
                  </Box>
                </Box>
              )}

              {!pollingAtivo && statusPagamento !== "timeout" && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Após efetuar o pagamento PIX, clique no botão "Realizei o
                  Pagamento" abaixo para verificar o status.
                </Alert>
              )}

              {pollingAtivo && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Verificando pagamento... Tentativa {tentativasPolling} de 5
                  <br />
                  <em>
                    Aguarde enquanto consultamos o status do seu pagamento
                  </em>
                </Alert>
              )}

              {statusPagamento === "timeout" && (
                <>
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <strong>Pagamento não detectado</strong>
                    <br />
                    Se você realizou o pagamento, tente verificar novamente ou
                    entre em contato com a secretaria da igreja pelo WhatsApp:
                    <br />
                    <strong>+55 (83) 8737-6137</strong>
                    <br />
                    <em>
                      Sua inscrição será processada manualmente se necessário.
                    </em>
                  </Alert>
                </>
              )}
            </Box>
          )}

          {/* Resumo da Inscrição Aprovada */}
          {inscricaoAprovada && (
            <Box sx={{ textAlign: "center" }}>
              {inscricaoAprovada.isPendente ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: "orange", mb: 1 }}>
                    ⏳ Inscrição Confirmada!
                  </Typography>
                  Sua inscrição foi registrada com sucesso.
                  <strong>
                    {" "}
                    Sua inscrição será confirmada no ato do pagamento.
                  </strong>
                  <br />
                  <strong>Forma de pagamento:</strong>{" "}
                  {inscricaoAprovada.formaPagamento === "cartao_credito"
                    ? "Cartão de Crédito"
                    : "Dinheiro"}
                  <br />
                  <em>
                    Entre em contato com a secretaria para finalizar o
                    pagamento.
                  </em>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: "green", mb: 1 }}>
                    🎉 Pagamento Aprovado!
                  </Typography>
                  Suas inscrições foram confirmadas com sucesso.
                </Alert>
              )}

              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "primary.main" }}
              >
                Resumo das Inscrições
              </Typography>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Pagante:</strong> {inscricaoAprovada.emailPagante}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Valor Total:</strong> R${" "}
                  {inscricaoAprovada.valorTotal.toFixed(2)}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>ID da Transação:</strong>{" "}
                  {inscricaoAprovada.transacaoId}
                </Typography>
              </Box>

              <Typography
                variant="subtitle1"
                sx={{ mt: 2, mb: 1, color: "primary.main" }}
              >
                <strong>Inscritos:</strong>
              </Typography>

              {inscricaoAprovada.inscricoes?.map((inscricao, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography variant="body1">
                    <strong>{inscricao.nomeInscrito}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inscricao.tipoProduto} - {inscricao.subtipoProduto}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "primary.main" }}>
                    R$ {inscricao.valorProduto.toFixed(2)}
                  </Typography>
                </Box>
              ))}

              <Alert severity="info" sx={{ mt: 2 }}>
                {inscricaoAprovada.isPendente
                  ? "Guarde este comprovante para consultas futuras. Entre em contato com a secretaria da igreja para finalizar o pagamento."
                  : "Um comprovante será enviado para seu email. Guarde o ID da transação para consultas futuras."}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!inscricaoAprovada &&
            !pollingAtivo &&
            statusPagamento !== "timeout" && (
              <Button
                variant="contained"
                onClick={iniciarPolling}
                disabled={pollingAtivo}
                sx={{
                  bgcolor: "#28a745",
                  "&:hover": { bgcolor: "#218838" },
                  mr: 1,
                }}
              >
                Realizei o Pagamento
              </Button>
            )}

          {!inscricaoAprovada &&
            !pollingAtivo &&
            statusPagamento === "timeout" && (
              <Button
                variant="contained"
                onClick={iniciarPolling}
                disabled={pollingAtivo}
                sx={{
                  bgcolor: "#ffa726",
                  "&:hover": { bgcolor: "#ff9800" },
                  mr: 1,
                }}
              >
                Tentar Novamente
              </Button>
            )}

          {inscricaoAprovada && (
            <Button
              variant="contained"
              onClick={() => window.print()}
              sx={{
                bgcolor: "primary.main",
                "&:hover": { bgcolor: "primary.dark" },
                mr: 1,
              }}
            >
              {inscricaoAprovada.isPendente
                ? "Salvar Comprovante de Inscrição"
                : "Salvar Comprovante"}
            </Button>
          )}

          <Button onClick={fecharModal} sx={{ color: "primary.main" }}>
            {inscricaoAprovada ? "Concluir" : "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Informações dos Produtos */}
      {TOGLE_SHOW_PRODUCTS_INFO && (
        <Dialog
          open={showProdutosInfo}
          onClose={() => setShowProdutosInfo(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              color: "primary.main",
              fontWeight: "bold",
              position: "relative",
            }}
          >
            Sobre os Produtos
            <IconButton
              onClick={() => setShowProdutosInfo(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "grey.500",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ p: 1 }}>
              {[
                {
                  /* {
                tipo: "Conf de Oração 2026 - Família",
                descricao: "A partir de 3 pagantes",
                preco: "R$ 30,00 cada",
              }, */
                },
              ].map((item, index) => (
                <Card
                  key={index}
                  sx={{
                    mb: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    "&:hover": {
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "primary.main",
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                          }}
                        >
                          {item.tipo}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            mt: 0.5,
                          }}
                        >
                          {item.descricao}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: "primary.main",
                          fontWeight: "bold",
                          fontSize: "1.2rem",
                        }}
                      >
                        {item.preco}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowProdutosInfo(false)}
              variant="contained"
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Entendi
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default InscricoesBase;
